"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@repo/ui";
import { BooleanField } from "@repo/ui/components/field-types";
import { AccordionDisclosure } from "@repo/ui/components/design-system/accordion/accordion-disclosure";
import { useConsent } from "@shared/hooks/cookie-consent";
import type { ConsentCategory } from "@shared/lib/consent/constants";
import {
	CONSENT_CATEGORIES,
	DEFAULT_CONSENT_CATS,
} from "@shared/lib/consent/constants";
import { PrimaryCta } from "@repo/ui/components/design-system/cta/primary-cta";
import { SecondaryCta } from "@repo/ui/components/design-system/cta/secondary-cta";
import { Z_INDEX } from "@shared/lib/z-index";
import { useTranslations } from "next-intl";
import * as React from "react";

const NON_ESSENTIAL: ConsentCategory[] = [
	"preferences",
	"analytics",
	"marketing",
];

function masterVisualState(draft: Record<ConsentCategory, boolean>): {
	allOn: boolean;
	mixed: boolean;
} {
	const vals = NON_ESSENTIAL.map((k) => draft[k]);
	const allOn = vals.every(Boolean);
	const mixed = !allOn && !vals.every((v) => !v);
	return { allOn, mixed };
}

function CategoryBody({
	baseKey,
}: {
	baseKey: "necessary" | "preferences" | "analytics" | "marketing";
}) {
	const t = useTranslations(`cookies.preferences.${baseKey}`);
	return (
		<div className="space-y-3 text-muted-foreground text-sm">
			<p>{t("description")}</p>
			<div>
				<p className="font-semibold text-foreground">
					{t("itemsTitle")}
				</p>
				<p className="mt-1 whitespace-pre-line">{t("items")}</p>
			</div>
			<p>{t("retention")}</p>
			<p>{t("thirdParties")}</p>
		</div>
	);
}

export function ConsentPreferencesDialog() {
	const tRoot = useTranslations("cookies.preferences");
	const { record, dialogOpen, close, savePartial, dialogDraftSeed } =
		useConsent();

	const masterSwitchId = React.useId();

	const [draft, setDraft] =
		React.useState<Record<ConsentCategory, boolean>>(DEFAULT_CONSENT_CATS);
	const [baseline, setBaseline] =
		React.useState<Record<ConsentCategory, boolean>>(DEFAULT_CONSENT_CATS);

	React.useEffect(() => {
		if (!dialogOpen) {
			return;
		}
		const base = record?.cats ?? DEFAULT_CONSENT_CATS;
		const normalized = {
			...DEFAULT_CONSENT_CATS,
			...base,
			necessary: true,
		};
		setBaseline(normalized);
		setDraft({
			...normalized,
			...dialogDraftSeed,
			necessary: true,
		});
		requestAnimationFrame(() => {
			document.getElementById(masterSwitchId)?.focus();
		});
	}, [dialogOpen, record, dialogDraftSeed]);

	const { allOn, mixed } = masterVisualState(draft);

	const isDirty = React.useMemo(
		() => CONSENT_CATEGORIES.some((c) => draft[c] !== baseline[c]),
		[draft, baseline],
	);

	const setMaster = React.useCallback((checked: boolean) => {
		setDraft((d) => ({
			...d,
			preferences: checked,
			analytics: checked,
			marketing: checked,
			necessary: true,
		}));
	}, []);

	const onDialogOpenChange = React.useCallback(
		(next: boolean) => {
			if (!next) {
				close();
			}
		},
		[close],
	);

	return (
		<DialogPrimitive.Root
			open={dialogOpen}
			onOpenChange={onDialogOpenChange}
		>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay
					className={cn(
						"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 bg-black/80 backdrop-blur-xs data-[state=closed]:animate-out data-[state=open]:animate-in",
					)}
					style={{ zIndex: Z_INDEX.consent.backdrop }}
				/>
				<DialogPrimitive.Content
					className={cn(
						"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] flex max-h-[90vh] w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col gap-6 overflow-y-auto rounded-lg border bg-background p-6 shadow-lg duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in sm:w-full",
					)}
					style={{ zIndex: Z_INDEX.consent.dialog }}
				>
					<div>
						<DialogPrimitive.Title className="font-black text-lg leading-none tracking-wide uppercase">
							{tRoot("title")}
						</DialogPrimitive.Title>
						<DialogPrimitive.Description className="mt-6 text-muted-foreground text-sm">
							{tRoot("description")}
						</DialogPrimitive.Description>
					</div>

					<BooleanField
						id={masterSwitchId}
						label={tRoot("enableAll")}
						value={allOn}
						mixed={mixed}
						mixedTooltip={tRoot("someEnabled")}
						presentation="switch"
						trueLabel=""
						falseLabel=""
						onChange={setMaster}
						className="rounded-lg border border-border p-4 [&_[data-field-shell]]:flex [&_[data-field-shell]]:items-center [&_[data-field-shell]]:justify-between [&_[data-field-shell]>div:first-child]:mb-0 [&_span.text-sm]:hidden"
					/>

					<dl className="divide-y divide-border">
						<AccordionDisclosure
							header={
								<span className="font-semibold text-base leading-7">
									{tRoot("necessary.title")}
								</span>
							}
							headerEnd={
								<span className="inline-flex items-center rounded-full bg-muted px-2 py-1 text-muted-foreground text-xs font-semibold leading-none">
									{tRoot("necessary.alwaysOn")}
								</span>
							}
							triggerClassName="py-6"
						>
							<div className="pb-6">
								<CategoryBody baseKey="necessary" />
							</div>
						</AccordionDisclosure>

						{(
							["preferences", "analytics", "marketing"] as const
						).map((cat) => (
							<AccordionDisclosure
								key={cat}
								header={
									<span className="font-semibold text-base leading-7">
										{tRoot(`${cat}.title`)}
									</span>
								}
								headerEnd={
									<BooleanField
										id={`consent-${cat}`}
										label={tRoot(`${cat}.title`)}
										value={draft[cat]}
										presentation="switch"
										trueLabel=""
										falseLabel=""
										onChange={(checked) =>
											setDraft((d) => ({
												...d,
												[cat]: checked,
											}))
										}
										className="w-auto [&_[data-field-shell]>div:first-child]:sr-only [&_[data-field-shell]>div:first-child]:mb-0 [&_span.text-sm]:hidden"
									/>
								}
								triggerClassName="py-6"
							>
								<div className="pb-6">
									<CategoryBody baseKey={cat} />
								</div>
							</AccordionDisclosure>
						))}
					</dl>

					<div className="flex flex-col gap-2 lg:flex-row lg:gap-3 [&>*]:min-w-0 lg:[&>*]:flex-1">
						<SecondaryCta
							type="button"
							appearance="plain"
							className="w-full"
							ctaSize="medium"
							onClick={() => close()}
						>
							{tRoot("cancel")}
						</SecondaryCta>
						<PrimaryCta
							type="button"
							className="w-full"
							ctaSize="medium"
							disabled={!isDirty}
							onClick={() =>
								savePartial({
									preferences: draft.preferences,
									analytics: draft.analytics,
									marketing: draft.marketing,
								})
							}
						>
							{tRoot("save")}
						</PrimaryCta>
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
